import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { OrgProvider, useOrg } from './context/OrgContext'
import { ZoomOAuthProvider } from './context/ZoomOAuthContext'
import { BillingProvider } from './context/BillingContext'
import { ToastProvider } from './components/common/Toast'
import ErrorBoundary from './components/common/ErrorBoundary'
import ProtectedRoute from './components/common/ProtectedRoute'
import PublicRoute from './components/common/PublicRoute'
import VerifyEmailRoute from './components/common/VerifyEmailRoute'
import AnalyticsRouteListener from './components/AnalyticsRouteListener'
import ScrollToTop from './components/common/ScrollToTop'
import SsoCompletionGate from './components/common/SsoCompletionGate'
import CustomerLoginPage from './customer/CustomerLoginPage'
import CustomerSignupPage from './customer/CustomerSignupPage'
import CustomerVerifyEmailPage from './customer/CustomerVerifyEmailPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ForgotPasswordOtpPage from './pages/ForgotPasswordOtpPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import JoinPage from './pages/JoinPage'
import CustomerJoinPage from './customer/CustomerJoinPage'
import CustomerMyAgentsPage from './customer/CustomerMyAgentsPage'
import { EMAIL_FLOWS, CUSTOMER_MEMBER_HOME, customerHome } from './customerBuild'
import { CustomerAppShell } from './customer/CustomerShellNavigation'
import { useOrgName } from './customer/session'
import { LoadingBlock } from './customer/shell/Notice'
import { RithmoWordmark } from './customer/shell/OrgMark'
import { CustomerSessionProvider } from './customer/cb/auth/AuthContext'

// The Control Board pages (customer/cb, owned here; see its README.md), over Rithmo's session and API.
const DashboardPage = lazy(() => import('./customer/cb/pages/controlBoard/DashboardPage'))
const AgentsPage = lazy(() => import('./customer/cb/pages/controlBoard/AgentsPage'))
const AgentDetailPage = lazy(() => import('./customer/cb/pages/controlBoard/AgentDetailPage'))
const ControlBoardConnectionsPage = lazy(() => import('./customer/cb/pages/controlBoard/ConnectionsPage'))
const AuthorityPage = lazy(() => import('./customer/cb/pages/controlBoard/AuthorityPage'))
// A member's own integrations (#437's catalog); admins reach it from Settings > Integrations.
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage'))
const McpAuthorizePage = lazy(() => import('./customer/CustomerMcpAuthorizePage'))
const SsoSettingsPage = lazy(() => import('./pages/SsoSettingsPage'))
const CustomerSettingsPage = lazy(() => import('./customer/CustomerSettingsPage'))

function Checking() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        <p className="text-gray-500">Loading...</p>
      </div>
    </div>
  )
}

const pageFallback = (
  <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>
)

/**
 * Shown instead of any signed-in page when this session's organization could not be resolved (the lookup failed, or it
 * answered with no organization). Without an organization there is no role, and no role must never read as "member".
 */
function OrgUnavailable() {
  const { logout } = useAuth()
  const { error, refetchOrg } = useOrg()
  const signedOut = error?.status === 401
  return (
    <div className="min-h-screen bg-gray-50 flex items-start sm:items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        {/* The Control Board sign-in page's mark (CustomerLoginPage). */}
        <div className="mb-6 flex items-center gap-3">
          <RithmoWordmark size={36} />
        </div>
        <h1 className="text-xl font-bold text-gray-900">{signedOut ? 'Your sign-in has ended' : "We couldn't open your organization"}</h1>
        <p className="text-gray-600 mt-2">
          {signedOut
            ? 'Sign in again to open your Control Board.'
            : "Rithmo couldn't load your organization or your access, so nothing is shown. Try again, or sign in again."}
        </p>
        {/* The Control Board sign-in page's own button styles (CustomerLoginPage). */}
        <div className="mt-6 space-y-3">
          <button type="button" onClick={logout} className="w-full rounded-xl bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">Sign in again</button>
          {!signedOut && <button type="button" onClick={refetchOrg} className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">Try again</button>}
        </div>
      </div>
    </div>
  )
}

/** Signed-in pages render only once this session's organization is known; otherwise the recovery screen above. */
function RequireOrg({ children }) {
  const { org, loading, resolved } = useOrg()
  if (loading || !resolved) return <Checking />
  return org?.id ? children : <OrgUnavailable />
}

/**
 * The Control Board pages: organization admins only — the rule /api/control-board enforces (403 not_admin).
 * Members go to Connections before any of those pages mounts, so nothing protected is requested. Unlike the shared
 * route guards it is never skipped in development, and it waits until the org role is known for this session.
 */
function OrgAdminRoute({ children }) {
  const { isAdmin } = useOrg()
  return <RequireOrg>{isAdmin ? children : <Navigate to={CUSTOMER_MEMBER_HOME} replace />}</RequireOrg>
}

/**
 * A Control Board page: its shell, and the signed-in person and organization from Rithmo's session (the pages' useAuth),
 * with its loading block while the page loads.
 */
function GovernancePage({ Page }) {
  const orgName = useOrgName()
  const { user, logout } = useAuth()
  const { org } = useOrg()
  const session = {
    user: user ? { name: user.name, email: user.email } : null,
    workspace: org?.id ? { id: org.id, name: orgName || 'Your organization' } : null,
    signOut: logout,
  }
  return (
    <CustomerAppShell>
      <CustomerSessionProvider value={session}>
        <Suspense fallback={<LoadingBlock label="Loading…" />}><Page /></Suspense>
      </CustomerSessionProvider>
    </CustomerAppShell>
  )
}

/** Admins: the organization's Connections (the Control Board's). Members: their own integrations. */
function ConnectionsRoute() {
  const { isAdmin } = useOrg()
  return (
    <RequireOrg>
      {isAdmin
        ? <GovernancePage Page={ControlBoardConnectionsPage} />
        : <Suspense fallback={pageFallback}><ConnectionsPage /></Suspense>}
    </RequireOrg>
  )
}

/** `/`: the signed-in home for this role (admins: Overview, members: Connections), or sign-in. */
function CustomerHome() {
  const { isAuthenticated, emailVerified, loading: authLoading } = useAuth()
  const { isAdmin } = useOrg()
  if (authLoading) return <Checking />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!emailVerified) return <Navigate to="/verify-email" replace />
  return <RequireOrg><Navigate to={customerHome(isAdmin)} replace /></RequireOrg>
}

/**
 * /dashboard: the Control Board's dashboard. Zoom's OAuth return also lands here (?zoom_connected=1); that one keeps
 * its query and goes on to Connections so the Zoom provider still finishes it.
 */
function DashboardRoute() {
  const { search } = useLocation()
  if (/[?&]zoom_(connected|error)=/.test(search)) return <Navigate to={{ pathname: '/connections', search }} replace />
  return <ProtectedRoute><OrgAdminRoute><GovernancePage Page={DashboardPage} /></OrgAdminRoute></ProtectedRoute>
}

/** Unverified accounts, when this deployment cannot send email: say so, and offer nothing that cannot complete. */
function EmailUnavailable() {
  const { logout } = useAuth()
  return (
    <div className="min-h-screen bg-gray-50 flex items-start sm:items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-8 flex items-center justify-center">
            <img src="/rithmo-mark.png" alt="Rithmo logo" className="w-full h-full object-contain" />
          </div>
          <span className="text-lg font-bold text-gray-900">Rithmo</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">Your email address isn't verified</h1>
        <p className="text-gray-600 mt-2">
          This Rithmo environment can't send verification emails. Ask your Rithmo administrator to verify your account, then sign in again.
        </p>
        <button type="button" onClick={logout} className="btn-secondary w-full mt-6">Sign out</button>
      </div>
    </div>
  )
}

function CustomerApp() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <OrgProvider>
        {/* The shared Sidebar and Settings read these two; the customer build shows no billing or Zoom-status UI. */}
        <BillingProvider>
        <ZoomOAuthProvider>
          <ToastProvider>
            <div className="min-h-screen bg-white">
            <ScrollToTop />
            {/* Shared sign-in: redeems an SSO completion code on whatever page the callback returns to. */}
            <SsoCompletionGate />
            <AnalyticsRouteListener />
            <Routes>
              <Route path="/" element={<CustomerHome />} />
              {/* The live Control Board's sign-in layout on Rithmo's own sign-in flow. */}
              <Route path="/login" element={<PublicRoute><CustomerLoginPage /></PublicRoute>} />
              {/* A company join link (?link=) joins the existing company; an emailed invitation (?token=) keeps its own page. */}
              <Route path="/join" element={<JoinRoute />} />
              {EMAIL_FLOWS && (
                <>
                  <Route path="/signup" element={<PublicRoute><CustomerSignupPage /></PublicRoute>} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/forgot-password/verify" element={<ForgotPasswordOtpPage />} />
                  <Route path="/forgot-password/reset" element={<ResetPasswordPage />} />
                </>
              )}
              <Route path="/verify-email" element={
                <VerifyEmailRoute>
                  {EMAIL_FLOWS ? <CustomerVerifyEmailPage /> : <EmailUnavailable />}
                </VerifyEmailRoute>
              } />
              {/* Connecting an AI assistant (hosted OAuth consent). Not a ProtectedRoute: it handles signed-in and signed-out itself and
                  keeps the authorization context across the login / SSO / invite / verify detour. */}
              <Route path="/mcp-authorize" element={<Suspense fallback={<div className="min-h-screen bg-gray-50" />}><McpAuthorizePage /></Suspense>} />

              <Route path="/dashboard" element={<DashboardRoute />} />
              {/* The Rithmo tab is gone (Tim, 2026-10-28); its page stays in the code. */}
              <Route path="/rithmo" element={<Navigate to="/dashboard" replace />} />
              {/* Holds open and close in the dashboard's side panel (Tim, 2026-10-28); the old tab's address goes there. */}
              <Route path="/holds" element={<Navigate to="/dashboard" replace />} />
              <Route path="/agents" element={<ProtectedRoute><OrgAdminRoute><GovernancePage Page={AgentsPage} /></OrgAdminRoute></ProtectedRoute>} />
              <Route path="/agents/:agentKey" element={<ProtectedRoute><OrgAdminRoute><GovernancePage Page={AgentDetailPage} /></OrgAdminRoute></ProtectedRoute>} />
              <Route path="/authority" element={<ProtectedRoute><OrgAdminRoute><GovernancePage Page={AuthorityPage} /></OrgAdminRoute></ProtectedRoute>} />
              <Route path="/connections" element={<ProtectedRoute><ConnectionsRoute /></ProtectedRoute>} />
              {/* The earlier customer pages' addresses land on their Control Board pages. */}
              <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
              <Route path="/activity" element={<Navigate to="/dashboard" replace />} />
              <Route path="/decisions" element={<Navigate to="/dashboard" replace />} />
              {/* Settings in the Control Board's shell; company sign-in (SSO) is the shared page inside the same shell. */}
              {/* Any member's own agents. */}
              <Route path="/my-agents" element={<ProtectedRoute><RequireOrg><GovernancePage Page={CustomerMyAgentsPage} /></RequireOrg></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><RequireOrg><GovernancePage Page={CustomerSettingsPage} /></RequireOrg></ProtectedRoute>} />
              <Route path="/settings/sso" element={<ProtectedRoute><RequireOrg><GovernancePage Page={SsoSettingsPage} /></RequireOrg></ProtectedRoute>} />

              {/* Every other page of the full dashboard does not exist in this build. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </div>
          </ToastProvider>
        </ZoomOAuthProvider>
        </BillingProvider>
        </OrgProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

/** /join: a company join link (?link=) -> the customer join page; an emailed invitation (?token=) -> the invitation page. */
function JoinRoute() {
  const [params] = useSearchParams()
  if (params.get('link')) return <CustomerJoinPage />
  return EMAIL_FLOWS ? <JoinPage /> : <Navigate to="/login" replace />
}

export default CustomerApp
